import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Ban,
  ClipboardCheck,
  Copy,
  FileCheck2,
  Pencil,
  RotateCcw,
  Send,
  UserRoundCog,
} from "lucide-react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { PackService } from "../../services/packService";
import Alert from "../components/Alert";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import FormField from "../components/FormField";
import PackExportPanel from "../components/PackExportPanel";
import PackHistoryTimeline from "../components/PackHistoryTimeline";
import PackViewContent from "../components/PackViewContent";
import PackViewLayout from "../components/PackViewLayout";
import PackViewSkeleton from "../components/PackViewSkeleton";
import Textarea from "../components/Textarea";
import { getPackStatusType } from "../components/packStatusType";
import { setDocumentMetadata } from "../components/RouteMetadata";
import { buildPackHistoryEvents } from "./packHistoryEvents";
import { getViewPackPermissions } from "./viewPackPermissions";

const errorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.data?.message || error?.message || fallback
);

const saveBase64 = (base64, filename, mime = "application/octet-stream") => {
  const link = document.createElement("a");
  link.href = "data:" + mime + ";base64," + base64;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const EXPORT_POLL_INTERVAL = 3000;

const exportInProgress = (status) => status === "pending" || status === "processing";

const ViewPack = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);

  const [pack, setPack] = useState(null);
  const [activeTab, setActiveTab] = useState("view");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [navigationAction, setNavigationAction] = useState(null);
  const [error, setError] = useState("");
  const [pdf, setPdf] = useState(null);
  const [exportLoaded, setExportLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [reason, setReason] = useState("");
  const loadRequestIdRef = useRef(0);
  const exportRunIdRef = useRef(0);

  const loadPack = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError("");
    setPack(null);
    setPdf(null);
    setExportLoaded(false);
    try {
      const response = await PackService.getPackById(id);
      if (requestId !== loadRequestIdRef.current) return;
      console.log("Pack data:", response);
      setPack(response);
      if (response?.pdf?.disk && response?.pdf?.filename) setPdf(response.pdf);
      const reference = response?.pack_reference
        || `WSP-${String(response?.pack_number || "").padStart(4, "0")}`;
      setDocumentMetadata(reference, `View details, exports and history for worksite pack ${reference}.`);
    } catch (requestError) {
      if (requestId === loadRequestIdRef.current) {
        setError(errorMessage(requestError, "The pack could not be loaded."));
      }
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPack();
    return () => { loadRequestIdRef.current += 1; };
  }, [loadPack]);

  useEffect(() => () => { exportRunIdRef.current += 1; }, [id]);

  const loadStoredExport = useCallback(async (packId, runId) => {
    const response = await PackService.latestExport(packId);
    if (runId !== exportRunIdRef.current) return;
    if (!response?.success || !response?.base64) {
      throw new Error(response?.message || "PDF generation failed.");
    }

    if (response.data) setPack(response.data);
    setPdf({
      base64: response.base64,
      failed_imports: response.failed_imports || [],
      filename: response.name || response.filename,
      name: response.name || response.filename,
      size: response.size,
      updated_at: response.updated_at,
    });
  }, []);

  // The PDF is generated in the background, so the request never waits on it.
  const waitForExport = useCallback(async (packId, runId) => {
    while (runId === exportRunIdRef.current) {
      const response = await PackService.exportStatus(packId);
      if (response?.status === "completed") return;
      if (!exportInProgress(response?.status)) {
        throw new Error(response?.message || "The PDF could not be generated.");
      }
      await new Promise((resolve) => { window.setTimeout(resolve, EXPORT_POLL_INTERVAL); });
    }
  }, []);

  const generateExport = useCallback(async (packId, alreadyStarted = false) => {
    const runId = ++exportRunIdRef.current;
    setGenerating(true);
    setError("");
    try {
      if (!alreadyStarted) await PackService.startExport(packId);
      await waitForExport(packId, runId);
      if (runId === exportRunIdRef.current) await loadStoredExport(packId, runId);
    } catch (requestError) {
      if (runId === exportRunIdRef.current) {
        setError(errorMessage(requestError, "The PDF could not be generated."));
      }
    } finally {
      if (runId === exportRunIdRef.current) setGenerating(false);
    }
  }, [loadStoredExport, waitForExport]);

  useEffect(() => {
    if (activeTab !== "export" || exportLoaded || !pack) return;

    const loadOrGenerateExport = async () => {
      const hasStoredExport = Boolean(pack.pdf?.disk && pack.pdf?.filename);
      const alreadyStarted = exportInProgress(pack.pdf?.status);
      setExportLoaded(true);

      if (hasStoredExport && !alreadyStarted) {
        const runId = ++exportRunIdRef.current;
        setWorking(true);
        setError("");
        try {
          await loadStoredExport(pack.id, runId);
          return;
        } catch (latestExportError) {
          if (runId !== exportRunIdRef.current) return;
          const message = errorMessage(latestExportError, "");
          if (!message.toLowerCase().includes("no export found")) {
            setPdf(null);
            setError(errorMessage(latestExportError, "The PDF could not be loaded or generated."));
            return;
          }
        } finally {
          setWorking(false);
        }
      }

      await generateExport(pack.id, alreadyStarted);
    };

    loadOrGenerateExport();
  }, [activeTab, exportLoaded, pack, generateExport, loadStoredExport]);

  const permissions = getViewPackPermissions(authUser, pack);

  const duplicatePack = async (edit) => {
    setNavigationAction(edit ? "edit" : "duplicate");
    setWorking(true);
    setError("");
    try {
      const response = await PackService.duplicateForTemp(pack.id, edit);
      if (response?.error) throw new Error(response.error);
      navigate("/packs/create", { state: { data: response.data } });
    } catch (requestError) {
      setError(errorMessage(requestError, edit ? "The pack could not be opened for editing." : "The pack could not be duplicated."));
    } finally {
      setNavigationAction(null);
      setWorking(false);
    }
  };

  const openStatusAction = (target, title, description, options = {}) => {
    setReason("");
    setStatusAction({ target, title, description, ...options });
  };

  const updateStatus = async () => {
    if (statusAction.requiresReason && !reason.trim()) return;
    setWorking(true);
    setError("");
    try {
      const response = await PackService.updateStatus(pack.id, {
        current_status: pack.status,
        status: statusAction.target,
        reason: reason.trim() || undefined,
      });
      if (response?.success && response.data) setPack(response.data);
      else if (response?.success === false) throw new Error(response.message || response.error);
      setStatusAction(null);
      setReason("");
    } catch (requestError) {
      setError(errorMessage(requestError, "The pack status could not be updated."));
    } finally {
      setWorking(false);
    }
  };

  const generatePdf = () => generateExport(pack.id);

  const downloadUpload = async (upload) => {
    setWorking(true);
    try {
      const response = await PackService.getUpload(pack.id, upload);
      if (response?.success && response.base64) {
        saveBase64(response.base64, response.name || upload.name || upload.filename, response.mime_type);
      } else {
        throw new Error(response?.message || "Download failed.");
      }
    } catch (requestError) {
      setError(errorMessage(requestError, "The supporting file could not be downloaded."));
    } finally {
      setWorking(false);
    }
  };

  const historyEvents = useMemo(() => buildPackHistoryEvents(pack?.history), [pack?.history]);

  const tabs = [
    { id: "view", label: "Pack view" },
    { id: "export", label: "Export" },
    { id: "history", label: "History", count: historyEvents.length },
  ];


  const shell = (content) => content;

  if (loading) return shell(<PackViewSkeleton />);
  if (!pack) return shell(
    <div className="wsm-view-pack-load-error" role="alert">
      <AlertCircle aria-hidden="true" />
      <div>
        <strong>Unable to load pack</strong>
        <p>{error || "The requested pack was not found."}</p>
      </div>
      <Button onClick={loadPack} variant="outline">Try again</Button>
    </div>,
  );

  const title = pack.pack_reference || "WSP-" + String(pack.pack_number || "").padStart(4, "0");
  const plannedPeriod = pack.location?.planned_start_date
    ? moment(pack.location.planned_start_date).format("D MMM YYYY, HH:mm") + (pack.location?.planned_end_date ? " to " + moment(pack.location.planned_end_date).format("D MMM YYYY, HH:mm") : "")
    : "Not specified";

  const actions = (
    <>
      {permissions.canCheck && <Button disabled={working} icon={ClipboardCheck} onClick={() => openStatusAction("Checked", "Check this pack?", "The pack will be passed to its primary Engineering Supervisor.")}>Check pack</Button>}
      {permissions.canPublish && <Button disabled={working} icon={Send} onClick={() => openStatusAction("Published", "Publish this pack?", "The checked pack will be marked as published.")}>Publish pack</Button>}
      {permissions.canReject && <Button disabled={working} icon={RotateCcw} onClick={() => openStatusAction("Rejected", "Reject this pack?", "The planner will need to review the requested changes.", { requiresReason: true, reasonLabel: "Reason for rejection" })} variant="destructive">Reject pack</Button>}
      {permissions.canEdit && <Button disabled={working} icon={Pencil} loading={navigationAction === "edit"} loadingLabel="Opening pack..." onClick={() => duplicatePack(true)} variant="outline">Edit pack</Button>}
      {permissions.canReassign && <Button disabled={working} icon={UserRoundCog} onClick={() => navigate(`/view/${pack.id}/reassign`)} variant="outline">Reassign ES</Button>}
      {permissions.canDuplicate && <Button disabled={working} icon={Copy} loading={navigationAction === "duplicate"} loadingLabel="Duplicating..." onClick={() => duplicatePack(false)} variant="ghost">Duplicate</Button>}
      {permissions.canVoid && <Button disabled={working} icon={Ban} onClick={() => openStatusAction("Void", "Void this pack?", "The pack will remain available in the register and history.", { requiresReason: true, reasonLabel: "Reason for voiding" })} variant="destructive">Void pack</Button>}
      {permissions.canReinstate && <Button disabled={working} icon={FileCheck2} onClick={() => openStatusAction("Created", "Reinstate this pack?", "The pack will return to Created status.", { requiresReason: true, reasonLabel: "Reason for reinstating" })}>Reinstate pack</Button>}
    </>
  );

  return shell(
    <>
      {error && <Alert title="Pack action not completed" type="error">{error}</Alert>}
      <PackViewLayout
        actions={actions}
        activeTab={activeTab}
        breadcrumbs={[{ href: "/packs/register", label: "Packs register" }, { label: title }]}
        metadata={[
          { label: "Possession", value: pack.location?.possession_no || "Not specified" },
          { label: "Week", value: pack.location?.week_no || "Not specified" },
          { label: "Planned period", value: plannedPeriod },
        ]}
        onTabChange={setActiveTab}
        status={pack.status || "Not specified"}
        statusType={getPackStatusType(pack.status)}
        tabs={tabs}
        title={title}
      >
        {activeTab === "view" && <PackViewContent onDownload={downloadUpload} pack={pack} />}
        {activeTab === "export" && <PackExportPanel generating={generating || working} preparing={generating} onDownload={() => pdf?.base64 && saveBase64(pdf.base64, pdf.name || pdf.filename || title + ".pdf", "application/pdf")} onGenerate={generatePdf} pdf={pdf} />}
        {activeTab === "history" && <PackHistoryTimeline events={historyEvents} />}
      </PackViewLayout>

      <Dialog
        actions={<><Button onClick={() => setStatusAction(null)} variant="outline">Cancel</Button><Button disabled={statusAction?.requiresReason && !reason.trim()} loading={working} onClick={updateStatus} variant={statusAction?.target === "Void" || statusAction?.target === "Rejected" ? "destructive" : "primary"}>Confirm</Button></>}
        description={statusAction?.description}
        onClose={() => setStatusAction(null)}
        open={Boolean(statusAction)}
        size="small"
        title={statusAction?.title || "Change pack status"}
      >
        {statusAction?.requiresReason ? (
          <FormField id="pack-status-reason" label={statusAction.reasonLabel} required>
            {(props) => <Textarea {...props} onChange={(event) => setReason(event.target.value)} rows={4} value={reason} />}
          </FormField>
        ) : <p>This action will be recorded in the pack history.</p>}
      </Dialog>

    </>,
  );
};

export default ViewPack;
